import { useState, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { useAuth } from '../lib/auth-context';
import { FASTAPI_URL } from '@/lib/config';

export interface SiteflowItem {
  identifier: string;
  name: string;
  type?: string;
  customCode?: string;
  parentFamily?: { identifier: string; name: string } | null;
}

export interface UseSiteflowDataReturn {
  projects: SiteflowItem[];
  families: SiteflowItem[];
  loading: boolean;
  error: string | null;
  fetchAll: () => Promise<void>;
}

async function fetchLookup(
  endpoint: string,
  token: string,
  params?: Record<string, string>
): Promise<SiteflowItem[]> {
  const url = new URL(`${FASTAPI_URL}/siteflow/lookup/${endpoint}`, window.location.origin);
  if (params) {
    Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));
  }

  const response = await fetch(url.toString(), {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    const errData = await response.json().catch(() => ({ detail: `Failed to fetch ${endpoint}` }));
    throw new Error(errData.detail || `HTTP ${response.status}`);
  }

  const json = await response.json();
  return json.data ?? [];
}

export const useSiteflowData = (): UseSiteflowDataReturn => {
  const { user } = useAuth();
  const [projects, setProjects] = useState<SiteflowItem[]>([]);
  const [families, setFamilies] = useState<SiteflowItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    if (!user?.token) {
      setError('Not authenticated');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const [projectsData, familiesData] = await Promise.all([
        fetchLookup('projects', user.token),
        fetchLookup('families', user.token),
      ]);

      setProjects(projectsData);
      setFamilies(familiesData);
    } catch (err) {
      Sentry.captureException(err, {
        tags: { flow: 'siteflow-api', stage: 'lookup-data' },
      });
      const message = err instanceof Error ? err.message : 'Failed to load Siteflow data';
      setError(message);
      console.error('Error fetching Siteflow lookup data:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.token]);

  return {
    projects,
    families,
    loading,
    error,
    fetchAll,
  };
};
